import React from 'react';
import { FormControl, Select, MenuItem, Button, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  FilterLoading,
  FilterValue,
  filterInputSx,
  filterMenuProps,
  onlyButtonSx,
  optionItemSx,
  selectAllItemSx,
  summarizeSelection,
} from './ui/filterStyles';
import { useFilterContext } from '../context/FilterContext';

interface PlatformsFilterProps {
  orgId: number;
  selectedPlatforms: string[];
  onPlatformsChange: (platforms: string[]) => void;
}

export const PlatformsFilter: React.FC<PlatformsFilterProps> = ({
  orgId,
  selectedPlatforms,
  onPlatformsChange,
}) => {
  const { availablePlatforms, isInitialized } = useFilterContext();

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selected = typeof value === 'string' ? value.split(',') : value;
    onPlatformsChange(selected);
  };

  const handleOnly = (platformId: string) => {
    onPlatformsChange([platformId]);
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const allPlatformIds = availablePlatforms.map(platform => platform.id.toLowerCase());
    onPlatformsChange(allPlatformIds);
  };

  const platformOptions = availablePlatforms.map(p => ({ id: p.id.toLowerCase(), name: p.name }));

  if (!isInitialized) {
    return <FilterLoading label="platforms" />;
  }

  return (
    <FormControl fullWidth>
      <Select
        id="platforms-filter"
        multiple
        displayEmpty
        value={selectedPlatforms}
        onChange={handleChange}
        renderValue={(selected) => (
          <FilterValue label="Platforms" value={summarizeSelection(selected as string[], platformOptions)} />
        )}
        input={<OutlinedInput sx={filterInputSx} />}
        MenuProps={filterMenuProps}
      >
        <MenuItem onMouseDown={handleSelectAll} sx={selectAllItemSx}>
          Select all
        </MenuItem>

        {availablePlatforms.map(platform => (
          <MenuItem
            key={platform.id}
            value={platform.id.toLowerCase()}
            sx={optionItemSx}
          >
            <Checkbox size="small" checked={selectedPlatforms.indexOf(platform.id.toLowerCase()) > -1} sx={{ p: 0, mr: 1 }} />
            <ListItemText primary={platform.name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            <Button
              className="filter-only"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOnly(platform.id.toLowerCase());
              }}
              sx={onlyButtonSx}
            >
              Only
            </Button>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
